import * as React from 'react';
import { resolve } from 'inversify-react';
import { observable } from 'mobx';
import { observer } from 'mobx-react';
import { connect } from "react-redux";
import { Dispatch } from 'redux';

import { ConfigurationService } from 'src/core/services/configuration.service';
import { ContextService } from 'src/core/services/context.service';
import { ToastNotifications } from 'src/core/ui/toast';
import { ObjectUtils } from 'src/core/utils/object';
import { TourItineraryProductDataService } from 'src/app/products/tour-itineraries/form/tour-itinerary-product.data';
import { TourItineraryProductView } from 'src/app/products/tour-itineraries/form/parts/tour-itinerary-products.view';
import { actionCreators } from "src/store/products/product-form-nav-actions";

interface PropsFromState {
	products?: TourItineraryProductItem[];
}

interface PropsFromDispatch {
	productsLoaded: (itinerary: TourItineraryItem, products: TourItineraryProductItem[]) => any;
}


interface Props extends ComponentViewProps<TourItineraryItem> {
	mode: ControlMode;
	contextService: ContextService;
	product?: ProductItem;
	readOnly?: boolean;
	fromDate?: Date;
}

@observer
class TourItineraryProductController extends React.Component<Props & PropsFromDispatch & PropsFromState, {}> {
	@observable
	private dataService: TourItineraryProductDataService;

	@resolve
	private toast: ToastNotifications;

	@resolve
	private configService: ConfigurationService;

	public componentDidMount(): void {
		this.props.setBusy(true);
		this.loadData()
			.catch((error) => {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			})
			.then(() => {
				this.props.setBusy(false);
			});
	}

	public render() {
		if (this.dataService == null) {
			return null;
		}

		const { mode, model, product, setBusy } = this.props;
		const defaultLanguage = this.configService.configuration.defaultLanguage;

		return (
			<TourItineraryProductView
				mode={mode}
				model={this.dataService}
				setBusy={setBusy}
				createNew={this.createNew}
				product={product}
				itinerary={model}
				defaultLanguage={defaultLanguage}
				readOnly={this.props.readOnly}
				fromDate={this.props.fromDate}
			/>
		);
	}

	private async loadData(): Promise<void> {
		const { model, contextService, productsLoaded, products, setBusy } = this.props;
		this.dataService = TourItineraryProductDataService.make(contextService);

		if (products == null && model.id) {
			this.dataService.addFilter('tourItineraryId', 'eq', model.id);
			this.dataService.addSorting('dateFrom', 'asc', true);
			this.dataService.pageSize = 100;
			try {
				setBusy(true);
				const items = await this.dataService.load(query => query);
				productsLoaded(this.props.model, items.slice());
			} catch (error) {
				this.toast.showToast('Request failed', ObjectUtils.parseError(error), 'error');
			} finally {
				setBusy(false);
			}
		} else {
			this.dataService.items = products;
		}
	}

	private createNew = async () => {
		const { model, setBusy, productsLoaded } = this.props;
		setBusy(true);
		const config = await this.configService.current();
		const itineraryProduct = await this.dataService.add(config.defaultLanguage, model);
		productsLoaded(model, [itineraryProduct]);
		setBusy(false);

		return itineraryProduct;
	}
}

const mapStateToProps = (
	state: IApplicationState,
	props: Props
): PropsFromState => {
	const itinerary = state.otherLoadedEntities.find(e => e.entity == props.model.entity) as TourItineraryItem;
	return {
		products: itinerary == null ? undefined : itinerary.products,
	};
};

const mapDispatchToProps = (dispatch: Dispatch): PropsFromDispatch => ({
	productsLoaded: (
		itinerary: TourItineraryItem,
		products: TourItineraryProductItem[],
	) => dispatch(actionCreators.productFormRelationshipLoaded(itinerary, products, "products")),
});

const connected = connect(mapStateToProps, mapDispatchToProps)(TourItineraryProductController);
export { connected as TourItineraryProductController };



// WEBPACK FOOTER //
// ./src/app/products/tour-itineraries/form/parts/tour-itinerary-products.controller.tsx